import React, { useMemo } from 'react'
import './journey-table-well.scss'
import classnames from 'classnames'
import { Container } from 'typescript-ioc/es5'
import { observe } from 'mobx'
import { Moment } from 'moment'
import * as moment from 'moment-timezone'
import {
    InfoCircleOutlined,
    LoadingOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    TeamOutlined,
} from '@ant-design/icons'
import { Button as AntDButton, Flex, Input, Modal, Table, Tooltip, Select } from 'antd'
import { TablePaginationConfig } from 'antd/lib/table'
import { Key, SorterResult } from 'antd/lib/table/interface'

import { BetterComponent } from '../../../components/better-component/better-component'
import { Button, FilterBar, StatusBadge } from '@pushly/aqe/lib/components'
import type { FilterDefinition, ActiveFilter } from '@pushly/aqe/lib/components'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import { AppState } from '../../../stores/app'
import { AppService, JourneyService } from '../../../services'
import { isProxyEvent, simpleNotification, titleCase } from '../../../_utils/utils'
import {
    Journey,
    LiveJourneyStatusName,
    isJourneyCompleted,
    getJourneyEndBehavior,
    encodeEndBehaviorValue,
    decodeEndBehaviorValue,
} from '../types/journey-api-response'
import { BetterSelect } from '../../../components/better-select/better-select'
import { SHORT_DATE_FORMAT, TZ_PLATFORM_DEFAULT } from '../../../constants'
import { resolveJourneyDisplayTimeZone } from '../helpers/journey-time-zone'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { JourneyStatusBadge } from '../../../components/badges/journey-status-badge'
import { getCalculatedStatus, isJourneyActiveOrPaused } from '../../../components/journey-builder/helpers/journey'
import { confirmAndApplyJourneyStatusChange } from '../components/journey-status-change-confirmation'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { NoTranslate } from '../../../components/no-translate/no-translate'
import { SwLink } from '../../../components/sw-link/sw-link'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { AudienceBadge, AudienceBadgeAudience } from '../../../components/badges/audience-badge'
import { WebBadge } from '../../../components/badges/web-badge'
import { NativeIosBadge } from '../../../components/badges/native-ios-badge'
import { NativeAndroidBadge } from '../../../components/badges/native-android-badge'
import { getTriggerDisplayLabel } from '../trigger-config'
import { ExitBehavior, ExitStatus, TriggerType } from '@pushly/models/lib/enums/journeys'
import { IApiResponsePaginationLinks } from '../../../interfaces/api-response-pagination-links'
import SwSimpleLinkPagination from '../../../components/sw-simple-link-pagination/sw-simple-link-pagination'
import { InsightsService } from '../../../services/insights'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { ImpressionsColumnView } from '../../../components/impressions-column-view/impressions-column-view'
import { ClicksColumnView } from '../../../components/clicks-column-view/clicks-column-view'
import { PurchasesColumnView } from '../../../components/purchases-column-view/purchases-column-view'
import { RevenueColumnView } from '../../../components/revenue-column-view/revenue-column-view'
import { getDomainCurrencyCode, shouldShowJourneyPurchaseColumns } from '../../../_utils/domain'
import { UserPreferencesApi, useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { useService } from '@pushly/aqe/lib/hooks'
import { antdStaticApi } from '../../../providers/antd-app-provider/antd-static-api'

const DEFAULT_PAGE_SIZE = 15

interface IJourneyTableWellProps {
    showSearch?: boolean
    showRefresh?: boolean
    showHeader?: boolean
    showMeta?: boolean
    title?: string
    actions?: React.ReactNode
    showAdvancedRowActions?: boolean
    pageSize?: number
    defaultStatusFilters?: string[]
    userPreferences?: UserPreferencesApi
}

interface IJourneyStats {
    impressions?: number
    clicks?: number
    purchases?: number
    purchaseAmount?: number
    /** Purchases divided by clicks, as a decimal fraction. */
    purchaseRateDecimal?: number
}

/**
 * Purchase columns are shown for every journey on a domain that has e-commerce
 * configured, rather than only for the rows that happen to carry purchase data.
 * This list is paginated, so a data-driven check would add and remove columns as
 * the reader pages through it, which reads as a rendering fault rather than as
 * information about the journeys.
 *
 * Re-exported from the domain utils, where it lives so that the Journey Insights
 * tab can gate its own purchase columns on the same answer without pulling in
 * this module.
 */
export { shouldShowJourneyPurchaseColumns }

interface IJourneyTableWellState {
    domain: DomainDto
    loading: boolean
    selectedStatusFilters: string[]
    journeysLoaded: boolean
    journeys: Journey[]
    journeyStats: Record<number, IJourneyStats>
    page: number
    pageSize: number
    query?: string
    sortedInfo: SorterResult<Journey> | null
    paginationLinks?: IApiResponsePaginationLinks
}

export const JOURNEY_STATUS_FILTER_OPTIONS: { value: string; label: React.ReactNode }[] = [
    { value: 'DRAFT', label: <JourneyStatusBadge status="DRAFT" expanded hideTooltip /> },
    { value: 'SCHEDULED', label: <JourneyStatusBadge status="SCHEDULED" expanded hideTooltip /> },
    { value: 'RUNNING', label: <JourneyStatusBadge status="RUNNING" expanded hideTooltip /> },
    { value: 'PAUSED', label: <JourneyStatusBadge status="PAUSED" expanded hideTooltip /> },
    { value: 'COMPLETED', label: <JourneyStatusBadge status="COMPLETED" expanded hideTooltip /> },
]

/**
 * Reads the status filters a person saved. The API's RUNNING filter excludes paused journeys, so a
 * saved set that includes RUNNING is read as including PAUSED too: pausing a journey from its own
 * row must not remove it from the list the person is looking at. Resolves undefined when nothing
 * was saved, so the caller falls back to its defaults.
 */
export function readSavedJourneyStatusFilters(savedStatusFilters: string[] | null | undefined): string[] | undefined {
    if (!savedStatusFilters?.length) {
        return undefined
    }

    if (savedStatusFilters.includes('RUNNING') && !savedStatusFilters.includes('PAUSED')) {
        return [...savedStatusFilters, 'PAUSED']
    }

    return savedStatusFilters
}

/**
 * Maps the snake_case segment shape from the journey API response
 * to the camelCase AudienceBadgeAudience shape expected by AudienceBadge.
 */
function mapJourneyAudienceToBadge(journey: Journey): AudienceBadgeAudience {
    const audience = journey.configuration?.audience
    if (!audience) return {}

    const mapSegment = (s: { id: number; name: string; is_default: boolean; segment_type: string }) => ({
        id: s.id,
        name: s.name,
        isDefault: s.is_default,
        type: s.segment_type as any,
    })

    return {
        segments: (audience.segments ?? []).map(mapSegment),
        excludedSegments: (audience.excluded_segments ?? []).map(mapSegment),
    }
}

export class JourneyTableWell extends BetterComponent<IJourneyTableWellProps, IJourneyTableWellState> {
    private readonly appState: AppState
    private readonly appService: AppService
    private readonly journeyService: JourneyService
    private readonly insightsService: InsightsService

    private defaultStatusFilters: string[] = ['DRAFT', 'RUNNING', 'PAUSED', 'SCHEDULED', 'COMPLETED']

    private endBehaviorValue: string | undefined
    private disposeObservers: Array<() => void> = []
    private statusFilterDebounce: number | undefined

    public constructor(props: IJourneyTableWellProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.journeyService = Container.get(JourneyService)
        this.insightsService = Container.get(InsightsService)

        const savedFilters = readSavedJourneyStatusFilters(this.props.userPreferences?.get(['journey_status_filters']))
        const initialStatusFilters = this.props.defaultStatusFilters || savedFilters || this.defaultStatusFilters

        this.state = {
            domain: this.appState.currentDomain!,
            loading: true,
            selectedStatusFilters: initialStatusFilters,
            journeysLoaded: false,
            journeys: [],
            journeyStats: {},
            pageSize: this.props.pageSize ?? DEFAULT_PAGE_SIZE,
            page: 1,
            sortedInfo: {
                columnKey: 'created_at',
                order: 'descend',
            },
        }
    }

    public componentDidMount(): void {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.ensureJourneysForList())]

        if (!this.props.userPreferences?.isLoading) {
            // Preferences already loaded — apply and fetch
            this.applySavedStatusFilters()
            this.ensureJourneysForList()
        }
        // Otherwise wait for componentDidUpdate to catch isLoading → false
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount()
        clearTimeout(this.statusFilterDebounce)

        this.disposeObservers.forEach((f: any) => f())
    }

    public componentDidUpdate(prevProps: IJourneyTableWellProps): void {
        // When preferences finish loading, apply saved filters then fetch
        if (prevProps.userPreferences?.isLoading && !this.props.userPreferences?.isLoading) {
            this.applySavedStatusFilters()
            this.ensureJourneysForList()
        }
    }

    public render(): React.ReactNode {
        const showPurchaseColumns = shouldShowJourneyPurchaseColumns(this.state.domain)
        // One currency per domain, so the code is resolved once for the whole
        // table rather than re-derived for every row.
        const currencyCode = getDomainCurrencyCode(this.state.domain)

        return (
            // The stylesheet for this table is authored against an ancestor element rather
            // than the table itself, because antd applies a consumer className to the table
            // wrapper — leaving its descendant selectors nothing above them to match.
            <div className="journey-table-well">
                <Table
                    className="journey-list-table aqe-table-well"
                    rowClassName="journey-list-row"
                    size="small"
                    title={() => this.renderHeader()}
                    dataSource={this.state.journeys}
                    onChange={this.handleTableChange}
                    pagination={false}
                    loading={
                        this.state.loading && {
                            indicator: <LoadingOutlined spin={true} />,
                        }
                    }
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={async (_ev, _link, config) => {
                                await this.setCurrentPage(config.current)
                                this.fetchJourneys()
                            }}
                            onNext={async (_ev, _link, config) => {
                                await this.setCurrentPage(config.current)
                                this.fetchJourneys()
                            }}
                        />
                    )}
                >
                    <Table.Column className="journey" title="Journey" render={this.renderDisplay} />
                    <Table.Column className="delivery" title="Run Dates" render={this.renderRunDates} align="center" />

                    <Table.Column
                        key="impressions"
                        className="total-impressions"
                        title="Impressions"
                        align="center"
                        render={(_: any, journey: Journey) => this.renderImpressionsCell(journey)}
                    />

                    <Table.Column
                        key="clicks"
                        className="total-clicks"
                        title={
                            <React.Fragment>
                                Clicks <span className="column-title-qualifier">(CTR)</span>
                            </React.Fragment>
                        }
                        align="center"
                        render={(_: any, journey: Journey) => this.renderClicksCell(journey)}
                    />

                    {showPurchaseColumns && (
                        <Table.Column
                            key="purchases"
                            className="total-purchases"
                            title={
                                <React.Fragment>
                                    Purchases <span className="column-title-qualifier">(Rate)</span>
                                </React.Fragment>
                            }
                            align="center"
                            render={(_: any, journey: Journey) => this.renderPurchasesCell(journey)}
                        />
                    )}

                    {showPurchaseColumns && (
                        <Table.Column
                            key="revenue"
                            className="total-revenue"
                            title="Revenue"
                            align="center"
                            render={(_: any, journey: Journey) => this.renderRevenueCell(journey, currencyCode)}
                        />
                    )}

                    <Table.Column className="actions" title="Actions" align="right" render={this.renderRowActions} />
                </Table>
            </div>
        )
    }

    /**
     * Metrics a journey actually has something to say about. A draft has never
     * run, and a journey the metrics fetch returned no row for has no numbers to
     * report; both cases leave the metric cells with an em dash.
     */
    protected getReportableJourneyStats(journey: Journey): IJourneyStats | undefined {
        if (journey.status_id === StatusType.DRAFT.id) return undefined

        return this.state.journeyStats[journey.id]
    }

    protected renderImpressionsCell(journey: Journey): React.ReactNode {
        const stats = this.getReportableJourneyStats(journey)
        if (!stats) return <span>—</span>

        return <ImpressionsColumnView impressions={stats.impressions ?? 0} status={journey.status} />
    }

    protected renderClicksCell(journey: Journey): React.ReactNode {
        const stats = this.getReportableJourneyStats(journey)
        if (!stats) return <span>—</span>

        const impressions = stats.impressions ?? 0
        const clicks = stats.clicks ?? 0

        return (
            <ClicksColumnView
                clicks={clicks}
                ctrDecimal={impressions === 0 ? 0 : clicks / impressions}
                status={journey.status}
            />
        )
    }

    protected renderPurchasesCell(journey: Journey): React.ReactNode {
        const stats = this.getReportableJourneyStats(journey)
        if (!stats) return <span>—</span>

        return <PurchasesColumnView purchases={stats.purchases ?? 0} purchaseRateDecimal={stats.purchaseRateDecimal} />
    }

    protected renderRevenueCell(journey: Journey, currencyCode: string): React.ReactNode {
        const stats = this.getReportableJourneyStats(journey)
        if (!stats) return <span>—</span>

        return <RevenueColumnView amount={stats.purchaseAmount ?? 0} currencyCode={currencyCode} />
    }

    protected renderHeader(): React.ReactNode {
        return (
            <JourneyTableHeader
                showSearch={this.props.showSearch}
                showRefresh={this.props.showRefresh}
                loading={this.state.loading}
                selectedStatusFilters={this.state.selectedStatusFilters}
                onSearch={this.searchJourneys}
                onStatusFilterChange={async (value: string[]) => {
                    this.props.userPreferences?.setDebounced(['journey_status_filters'], value)
                    await this.setState({ selectedStatusFilters: value, page: 1 })
                    clearTimeout(this.statusFilterDebounce)
                    this.statusFilterDebounce = window.setTimeout(() => this.fetchJourneys(), 800)
                }}
                onRefresh={this.refreshJourneys}
            />
        )
    }

    protected renderDisplay = (_text: string, journey: Journey): React.ReactNode => {
        const name = journey.name
        const statusText = getCalculatedStatus(journey, (status) => {
            return status === 'ACTIVE' ? 'RUNNING' : status
        })

        const computedType = getTriggerDisplayLabel(
            journey.configuration.computed_trigger_type,
            titleCase(journey.type),
        )

        const audience = mapJourneyAudienceToBadge(journey)
        const hasSegments = (audience.segments?.length ?? 0) > 0 || (audience.excludedSegments?.length ?? 0) > 0
        const channels = journey.channels ?? []

        let audienceNode: React.ReactNode
        if (!hasSegments && journey.configuration?.computed_trigger_type === TriggerType.SUBSCRIBED) {
            audienceNode = (
                <span className="audience-type">
                    <span className="audience-badge">
                        <span className="included-badge">
                            <span className="included-icon">
                                <TeamOutlined />
                            </span>
                            <span>All New Subscribers</span>
                        </span>
                    </span>
                </span>
            )
        } else {
            audienceNode = (
                <span className="audience-type">
                    <AudienceBadge audience={audience} />
                </span>
            )
        }

        return (
            <TableRowEntityDisplay
                title={
                    <SwLink href={this.buildDetailsUrl(journey)}>
                        <NoTranslate>{name}</NoTranslate>
                    </SwLink>
                }
                description={<span>Type: {computedType}</span>}
                status={<StatusBadge type="campaign" status={statusText} expanded={true} />}
                // status={<JourneyStatusBadge status={statusText} expanded={true} />}
                audience={audienceNode}
                badges={
                    <>
                        {channels.includes(DeliveryChannel.WEB) && <WebBadge />}
                        {channels.includes(DeliveryChannel.NATIVE_IOS) && <NativeIosBadge />}
                        {channels.includes(DeliveryChannel.NATIVE_ANDROID) && <NativeAndroidBadge />}
                    </>
                }
            />
        )
    }

    protected renderRunDates = (_: string, journey: Journey): React.ReactNode => {
        const domainTz = this.state.domain?.timezone ?? TZ_PLATFORM_DEFAULT
        const { tz } = resolveJourneyDisplayTimeZone(journey.date_time_zone, this.state.domain)

        const dateStart: Moment = moment.tz(journey.date_start, tz)
        const dateStartStr: string = dateStart.format(SHORT_DATE_FORMAT)

        let dateEnd: Moment | null = null
        let dateEndStr: string | null = null
        if (journey.date_end) {
            dateEnd = moment.tz(journey.date_end, tz)
        } else if (journey.date_completed_utc) {
            dateEnd = moment.tz(journey.date_completed_utc, 'UTC').tz(domainTz)
        }
        if (dateEnd) {
            dateEndStr = dateEnd.format(SHORT_DATE_FORMAT)
        }

        return (
            <div className="composite-stack">
                <span>{dateStartStr}</span>
                {!dateEndStr ? (
                    <span>Ongoing</span>
                ) : (
                    <span>
                        <span className="lowlight">through</span> {dateEndStr}
                    </span>
                )}
            </div>
        )
    }

    protected renderRowActions = (journey: Journey): React.ReactNode => {
        const { abilityStore } = this.appState
        const canMutate = abilityStore.can(
            AbilityAction.UPDATE,
            abilityStore.getDomainOwnedIdentityFor(SubjectEntity.CAMPAIGN),
        )

        const isDraft = journey.status === StatusType.DRAFT.name
        const isCancelled = journey.status === StatusType.CANCELLED.name
        const isCompleted =
            journey.status === StatusType.COMPLETED.name || getCalculatedStatus(journey) === StatusType.COMPLETED.name

        const canBeArchived = isDraft || isCancelled || isCompleted

        let actions: any = []
        if (this.props.showAdvancedRowActions === false) {
            actions = undefined
        } else if (canMutate) {
            if (!isCompleted || isDraft) {
                actions.push({
                    text: 'Edit',
                    icon: 'edit',
                    altHref: this.buildEditUrl(journey),
                    onClick: this.handleEditClick,
                    journey,
                })
            }

            actions.push({
                text: 'Duplicate',
                icon: 'copy',
                altHref: this.buildDuplicateUrl(journey),
                onClick: this.handleDuplicateClick,
                journey,
            })

            if (!isDraft && !isJourneyCompleted(journey)) {
                actions.push({ divider: true })

                if (isJourneyActiveOrPaused(journey)) {
                    const journeyIsPaused = journey.status === StatusType.PAUSED.name
                    actions.push({
                        text: journeyIsPaused ? 'Resume' : 'Pause',
                        icon: journeyIsPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />,
                        onClick: () => this.handlePauseOrResumeClick(journey, journeyIsPaused ? 'ACTIVE' : 'PAUSED'),
                        journey,
                    })
                }

                actions.push({
                    text: 'Complete',
                    icon: 'info-circle',
                    onClick: () => this.handleComplete(journey),
                    journey,
                })
            }

            if (canBeArchived) {
                actions.push({ divider: true })
                actions.push({
                    text: 'Archive',
                    icon: 'folder',
                    onClick: this.handleArchiveClick,
                    journey,
                    confirm: {
                        okText: 'Yes, Archive',
                        title: 'Archive Journey',
                        className: 'completed-journey-confirm',
                        iconType: 'info-circle',
                        content: (
                            <div>
                                Are you sure you want to archive the following journey?
                                <p>
                                    <b>{journey.name}</b>
                                </p>
                            </div>
                        ),
                    },
                })
            }
        }

        return (
            <Button
                actions={actions}
                onClick={() => this.handlePrimaryActionClick(journey)}
                size="small"
                altHref={this.buildDetailsUrl(journey)}
            >
                <span>View</span>
            </Button>
        )
    }

    protected handleComplete = async (journey: Journey) => {
        const journeyBehavior = getJourneyEndBehavior(journey)
        const initialValue = encodeEndBehaviorValue(journeyBehavior)
        this.endBehaviorValue = initialValue

        antdStaticApi.modal.confirm({
            title: 'Complete Journey',
            className: 'journey-completed-confirm',
            icon: <InfoCircleOutlined />,
            okText: 'Yes, Complete',
            width: 425,
            content: (
                <div className="end-date-behavior">
                    <span className="end-date-behavior-label">
                        When the journey completes, all current active members will:
                        <Tooltip
                            title={
                                <>
                                    No additional subscribers will be added to the journey when it is completed. Choose
                                    one of the options to determine how members who are currently active in the journey
                                    will be treated when the journey is complete.
                                </>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </span>
                    <div>
                        <Select
                            className="end-date-behavior-select"
                            defaultValue={journeyBehavior?.behavior?.toUpperCase() ?? ExitBehavior.BLEED}
                            onChange={(val: string) => {
                                this.endBehaviorValue = val
                            }}
                            options={[
                                {
                                    value: ExitBehavior.BLEED,
                                    label: 'Continue executing steps of the journey',
                                },
                                {
                                    value: `${ExitBehavior.STOP}:${ExitStatus.COMPLETED}`,
                                    label: 'Stop executing steps and have their status set to "Completed"',
                                },
                                {
                                    value: `${ExitBehavior.STOP}:${ExitStatus.REMOVED}`,
                                    label: 'Stop executing steps and have their status set to "Removed"',
                                },
                            ]}
                            disabled={isJourneyCompleted(journey)}
                            styles={{
                                root: { width: '100%', marginTop: 6 },
                                popup: { root: { width: 'fit-content' } },
                            }}
                        />
                    </div>
                </div>
            ),
            onOk: () => this.handleCompleteClick(journey),
        })
    }

    protected handlePauseOrResumeClick = (journey: Journey, targetStatus: LiveJourneyStatusName): Promise<void> =>
        confirmAndApplyJourneyStatusChange({
            journey,
            targetStatus,
            domain: this.state.domain,
            onStatusChanged: this.refreshJourneys,
        })

    protected buildDetailsUrl(journey: Journey): string {
        const url = `/journeys/${journey.id}/summary`
        return this.appService.routeWithinDomain(url, true)
    }

    protected buildDuplicateUrl(journey: Journey): string {
        const url = `/journeys/new?template=${journey.id}`
        return this.appService.routeWithinDomain(url, true)
    }

    protected buildEditUrl(journey: Journey): string {
        const url = `/journeys/${journey.id}/builder`
        return this.appService.routeWithinDomain(url, true)
    }

    protected handleTableChange = async (
        pagination: TablePaginationConfig,
        _filters: Record<string, Key[] | null>,
        sorter: SorterResult<Journey>,
    ): Promise<void> => {
        return this.setState({
            sortedInfo: sorter,
        })
    }

    protected handlePrimaryActionClick(journey: Journey): void {
        this.appService.route(this.buildDetailsUrl(journey))
    }

    protected handleEditClick = async (item: { props: { journey: Journey } }): Promise<void> => {
        const { journey } = item.props
        this.appService.route(this.buildEditUrl(journey))
    }

    protected handleDuplicateClick = async (item: { props: { journey: Journey } }): Promise<void> => {
        const { journey } = item.props
        this.appService.route(this.buildDuplicateUrl(journey))
    }

    protected handleCompleteClick = async (journey: Journey): Promise<void> => {
        const value = this.endBehaviorValue!
        const currentEncoded = encodeEndBehaviorValue(getJourneyEndBehavior(journey))

        if (value !== currentEncoded) {
            const decoded = decodeEndBehaviorValue(value)
            journey.configuration = {
                ...journey.configuration,
                end_behavior: decoded.behavior,
                end_status: decoded.status || ExitStatus.COMPLETED,
            }
        }

        try {
            await this.journeyService.patch(
                journey.id,
                {
                    status: StatusType.COMPLETED.name,
                    configuration: {
                        ...journey.configuration,
                    },
                },
                {
                    showLoadingScreen: true,
                    cancellationKey: 'handleCompleteClick',
                },
            )

            const journeys = [...this.state.journeys]
            const idx = journeys.findIndex((c) => c.id.toString() === journey.id.toString())
            if (idx !== -1) {
                const journeyUpdate = journeys[idx]
                journeyUpdate.status_id = StatusType.COMPLETED.id
                journeyUpdate.status = StatusType.COMPLETED.name
                journeys.splice(idx, 1, journeyUpdate)

                this.setState({ journeys })
            }

            simpleNotification(
                'success',
                'The journey has been successfully completed with the selected end date behavior.',
            )
        } catch (e) {
            if (e) {
                simpleNotification('error', e)
            }
        }

        return this.searchJourneys()
    }

    protected handleArchiveClick = async (item: { props: { journey: Journey } }): Promise<void> => {
        const { journey } = item.props

        await this.journeyService.destroy(journey.id, {
            showLoadingScreen: true,
            cancellationKey: 'handleCompleteClick',
        })

        return this.searchJourneys()
    }

    protected async setCurrentPage(page: number | undefined): Promise<void> {
        return this.setState({ page: page ?? 1 })
    }

    protected async ensureJourneysForList() {
        const { domain } = this.state

        if (this.state.journeysLoaded && !!domain && domain.id === this.appState.currentDomain!.id) {
            return
        }

        await this.setState({ loading: true, journeysLoaded: false })

        return this.fetchJourneys()
    }

    protected getSelectedStatusFilters(): string[] {
        return this.state.selectedStatusFilters || []
    }

    protected refreshJourneys = async () => {
        return this.fetchJourneys()
    }

    protected async fetchJourneys(search?: string): Promise<void> {
        const { pageSize, page, query } = this.state

        await this.setState({
            loading: true,
        })

        try {
            const res = await this.journeyService.fetchAll({
                cancellationKey: 'cl.fetchAllJourneys',
                query: {
                    limit: pageSize,
                    page,
                    search: search ? search : query,
                    status: this.getSelectedStatusFilters().join(','),
                },
            })

            if (res.ok && res.data.length > 0) {
                const journeys: Journey[] = res.data.map((item: any) => ({
                    ...item,
                    type: item.campaign_type ?? item.type,
                    key: item.id,
                }))

                // Fetch stats before showing the table to avoid pop-in
                // (matches notification list behavior)
                await this.fetchStats(journeys)

                await this.setState({
                    domain: this.appState.currentDomain!,
                    loading: false,
                    journeysLoaded: true,
                    journeys,
                    paginationLinks: res.meta?.links,
                })

                return
            } else {
                return this.setState({
                    domain: this.appState.currentDomain!,
                    loading: false,
                    journeysLoaded: true,
                    journeys: [],
                    journeyStats: {},
                    paginationLinks: res.meta?.links,
                })
            }
        } catch (_) {}
    }

    protected async fetchStats(journeys: Journey[]): Promise<void> {
        const domainId = this.appState.currentDomain?.id
        if (!domainId || journeys.length === 0) return

        const journeyIds = journeys.map((j) => j.id)

        try {
            const stats = await this.insightsService.fetch(
                {
                    entity: ReportEntity.CAMPAIGNS,
                    date_preset: ReportDatePreset.LIFETIME,
                    date_increment: ReportDateIncrement.LIFETIME,
                    breakdowns: ['campaign'],
                    fields: this.buildStatsFields(),
                    filters: [
                        {
                            field: 'domain.id',
                            operator: 'eq',
                            value: domainId,
                        },
                        {
                            field: 'campaign.id',
                            operator: 'in',
                            value: journeyIds,
                        },
                    ],
                },
                {
                    cancellationKey: 'cl.fetchJourneyListStats',
                },
            )

            if (Array.isArray(stats)) {
                const journeyStats: Record<number, IJourneyStats> = {}
                stats.forEach((stat: any) => {
                    const id = stat.campaign?.id ?? stat.campaign_id
                    if (id != null) {
                        journeyStats[id] = {
                            impressions: stat.impressions,
                            clicks: stat.clicks,
                            purchases: stat.purchases,
                            purchaseAmount: stat.purchase_amount,
                            purchaseRateDecimal: this.resolvePurchaseRateDecimal(stat),
                        }
                    }
                })

                this.setState({ journeyStats })
            }
        } catch (_) {
            // stats fetch failure should not block the list
        }
    }

    /**
     * Metric fields for the list's stats request. The purchase fields are asked
     * for only when the purchase columns will actually be rendered: one request
     * feeds every metric cell in the table and its failure is swallowed, so a
     * field a domain has no use for must not be able to blank out Impressions and
     * Clicks as well.
     */
    protected buildStatsFields(): string[] {
        const fields = ['campaign_id', 'impressions', 'clicks']
        if (shouldShowJourneyPurchaseColumns(this.appState.currentDomain)) {
            fields.push('purchases', 'purchase_amount', 'purchase_rate')
        }

        return fields
    }

    /**
     * The purchase rate is purchases over clicks. The reporting API is the
     * authority on it because it owns the attribution window — a purchase can be
     * attributed to an impression rather than a click, so the API's rate stands
     * even over zero clicks. It omits the field for rows it has no rate for, and
     * recomputing from the counts already in the row covers those.
     */
    protected resolvePurchaseRateDecimal(stat: any): number | undefined {
        if (stat.purchase_rate != null) return stat.purchase_rate
        if (!stat.clicks) return undefined

        return (stat.purchases ?? 0) / stat.clicks
    }

    protected searchJourneys = async (event?: string | React.SyntheticEvent) => {
        let value

        if (event) {
            value = isProxyEvent(event) ? event.target.value : event
        }

        const valueIsEmpty = !value || value.length === 0
        if (valueIsEmpty || value.length >= 2) {
            await this.setState({
                query: value,
                page: 1,
            })

            await this.fetchJourneys(value)
        }
    }

    private applySavedStatusFilters(): void {
        if (this.props.defaultStatusFilters) return // explicit defaults take priority

        const savedFilters = readSavedJourneyStatusFilters(this.props.userPreferences?.get(['journey_status_filters']))
        if (savedFilters) {
            this.setState({ selectedStatusFilters: savedFilters })
        }
    }
}

interface IJourneyTableHeaderProps {
    showSearch?: boolean
    showRefresh?: boolean
    loading: boolean
    selectedStatusFilters: string[]
    onSearch: (value?: string | React.SyntheticEvent) => void
    onStatusFilterChange: (value: string[]) => void
    onRefresh: () => void
}

const JourneyTableHeader: React.FC<IJourneyTableHeaderProps> = ({
    showSearch,
    showRefresh,
    loading,
    selectedStatusFilters,
    onSearch,
    onStatusFilterChange,
    onRefresh,
}) => {
    const filterDefinitions: FilterDefinition[] = useMemo(
        () => [
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: JOURNEY_STATUS_FILTER_OPTIONS,
            },
        ],
        [],
    )

    const getFilterBarValue = (): ActiveFilter[] => {
        if (selectedStatusFilters.length > 0) {
            return [{ key: 'status', operator: 'in', value: selectedStatusFilters }]
        }
        return []
    }

    const handleFilterBarApply = (appliedFilters: ActiveFilter[]) => {
        const statusFilter = appliedFilters.find((f) => f.key === 'status')
        const newStatus = (statusFilter?.value as string[]) || []
        onStatusFilterChange(newStatus)
    }

    return (
        <Flex className="journey-list-header" gap={8} justify="space-between">
            <Flex flex={1} gap={8} align="center">
                {showSearch !== false && (
                    <Input.Search
                        className="journey-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Journey Name"
                        enterButton={true}
                        onSearch={onSearch}
                    />
                )}

                <FilterBar
                    filterDefinitions={filterDefinitions}
                    value={getFilterBarValue()}
                    onApply={handleFilterBarApply}
                    showInlineTags={false}
                    showInlineClearAll={false}
                    size="middle"
                />
            </Flex>

            <div
                className="journey-list-refresh"
                style={{
                    borderLeft: '1px solid var(--aqe-color-border-default)',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                {showRefresh !== false && (
                    <Tooltip title="Refresh">
                        <Button
                            className="journey-list-refresh-toggle"
                            aria-label="Refresh journeys"
                            type="text"
                            shape="round"
                            icon={<ReloadOutlined className="text--size--large" spin={loading} />}
                            onClick={onRefresh}
                        />
                    </Tooltip>
                )}
            </div>
        </Flex>
    )
}

const JourneyTableWellWrapper = (props: IJourneyTableWellProps) => {
    const appState = useService(AppState)
    const userPreferences = useUserPreferences(appState.currentUser?.id, appState.currentDomain?.id)

    return <JourneyTableWell {...props} userPreferences={userPreferences} />
}

export default JourneyTableWellWrapper
